import React, { useRef, useState } from 'react';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Chip,
  ClickAwayListener,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Popper,
  Stack,
  Typography,
} from '@mui/material';

// --- 型定義 ---

export type SelectOption = {
  /** 値 (key) */
  value: string;
  /** 表示ラベル */
  label: string;
};

type CustomSelectBaseProps = {
  /** 選択肢リスト */
  options: SelectOption[];
  /** ラベル */
  label?: string;
  /** 必須表示 */
  required?: boolean;
  /** エラー状態 */
  error?: boolean;
  /** エラーメッセージ or ヘルパーテキスト */
  helperText?: string;
  /** 無効状態 */
  disabled?: boolean;
  /** プレースホルダ */
  placeholder?: string;
};

type CustomSelectSingleProps = CustomSelectBaseProps & {
  /** 選択モード: 単一 */
  mode: 'single';
  /** 選択中の値 */
  value: string;
  /** 選択変更ハンドラ（単一） */
  onChange: (value: string) => void;
};

type CustomSelectMultiProps = CustomSelectBaseProps & {
  /** 選択モード: 複数 */
  mode: 'multi';
  /** 選択中の値リスト */
  value: string[];
  /** 選択変更ハンドラ（複数） */
  onChange: (value: string[]) => void;
};

export type CustomSelectProps = CustomSelectSingleProps | CustomSelectMultiProps;

/**
 * カスタムセレクトボックス。
 *
 * - mode="single": 単一選択。選択後にメニューを閉じる。
 * - mode="multi": 複数選択。チェックボックスではなく選択状態の色でアイテムを示す。
 *   選択後もメニューが閉じないため連続選択が可能。
 */
const CustomSelect: React.FC<CustomSelectProps> = (props) => {
  const { options, label, required, error, helperText, disabled, placeholder } = props;
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const handleOpen = () => {
    if (!disabled) setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  // 共通のOutlinedInputスタイル
  const inputSx = {
    borderRadius: 2,
    cursor: disabled ? 'default' : 'pointer',
    '& input': { cursor: disabled ? 'default' : 'pointer' },
  };

  // 共通のArrowアイコン
  const arrowIcon = (
    <ArrowDropDownIcon
      sx={{
        color: disabled ? 'action.disabled' : 'action.active',
        transition: 'transform 0.2s',
        transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
        position: 'absolute',
        right: 8,
        top: '50%',
        marginTop: '-12px',
        pointerEvents: 'none',
      }}
    />
  );

  // --- 単一選択 ---
  if (props.mode === 'single') {
    const selectedOption = options.find((o) => o.value === props.value);

    const handleSelect = (value: string) => {
      props.onChange(value);
      handleClose();
    };

    return (
      <FormControl fullWidth size="small" error={error} required={required} disabled={disabled}>
        {label && <InputLabel shrink>{label}</InputLabel>}
        <Box ref={anchorRef} onClick={handleOpen} sx={{ position: 'relative' }}>
          <OutlinedInput
            label={label}
            readOnly
            value={selectedOption?.label || ''}
            placeholder={placeholder || '選択してください'}
            notched={!!label}
            size="small"
            fullWidth
            sx={inputSx}
          />
          {arrowIcon}
        </Box>
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
        <Popper
          open={open}
          anchorEl={anchorRef.current}
          placement="bottom-start"
          style={{ zIndex: 1300, width: anchorRef.current?.clientWidth }}
        >
          <ClickAwayListener onClickAway={handleClose}>
            <Paper
              elevation={4}
              sx={{ maxHeight: 240, overflow: 'auto', mt: 0.5, borderRadius: 2 }}
            >
              {options.map((option) => (
                <MenuItem
                  key={option.value}
                  selected={option.value === props.value}
                  onClick={() => handleSelect(option.value)}
                  sx={{ py: 1 }}
                >
                  <Typography variant="body2">{option.label}</Typography>
                </MenuItem>
              ))}
              {options.length === 0 && (
                <MenuItem disabled>
                  <Typography variant="body2" color="text.secondary">
                    選択肢がありません
                  </Typography>
                </MenuItem>
              )}
            </Paper>
          </ClickAwayListener>
        </Popper>
      </FormControl>
    );
  }

  // --- 複数選択 ---
  const selectedValues = new Set(props.value);
  const selectedOptions = options.filter((o) => selectedValues.has(o.value));

  const handleToggle = (value: string) => {
    if (selectedValues.has(value)) {
      props.onChange(props.value.filter((v) => v !== value));
    } else {
      props.onChange([...props.value, value]);
    }
  };

  const handleRemoveChip = (value: string) => {
    props.onChange(props.value.filter((v) => v !== value));
  };

  return (
    <FormControl fullWidth size="small" error={error} required={required} disabled={disabled}>
      {label && <InputLabel shrink>{label}</InputLabel>}
      <Box
        ref={anchorRef}
        onClick={handleOpen}
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          minHeight: 40,
          border: 1,
          borderColor: error ? 'error.main' : open ? 'primary.main' : 'divider',
          borderRadius: 2,
          px: 1.5,
          pr: 4,
          py: 0.5,
          cursor: disabled ? 'default' : 'pointer',
          bgcolor: disabled ? 'action.disabledBackground' : 'transparent',
          transition: 'border-color 0.2s',
          '&:hover': {
            borderColor: disabled ? 'divider' : error ? 'error.main' : 'text.primary',
          },
          // fieldset from label notch
          ...(label && {
            mt: '8px',
          }),
        }}
      >
        {selectedOptions.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ userSelect: 'none' }}>
            {placeholder || '選択してください'}
          </Typography>
        ) : (
          <Stack
            direction="row"
            spacing={0.5}
            flexWrap="wrap"
            useFlexGap
            sx={{ alignItems: 'center', py: 0.25 }}
          >
            {selectedOptions.map((option) => (
              <Chip
                key={option.value}
                label={option.label}
                size="small"
                onDelete={
                  disabled
                    ? undefined
                    : (e) => {
                        e.stopPropagation();
                        handleRemoveChip(option.value);
                      }
                }
                deleteIcon={<CloseIcon sx={{ fontSize: 14 }} />}
                sx={{ borderRadius: 1.5, fontWeight: 500 }}
              />
            ))}
          </Stack>
        )}
        {arrowIcon}
      </Box>
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        style={{ zIndex: 1300, width: anchorRef.current?.clientWidth }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper elevation={4} sx={{ maxHeight: 240, overflow: 'auto', mt: 0.5, borderRadius: 2 }}>
            {options.map((option) => {
              const isSelected = selectedValues.has(option.value);
              return (
                <MenuItem
                  key={option.value}
                  onClick={() => handleToggle(option.value)}
                  sx={{
                    py: 1,
                    backgroundColor: isSelected ? 'primary.light' : 'transparent',
                    color: isSelected ? 'primary.contrastText' : 'text.primary',
                    '&:hover': {
                      backgroundColor: isSelected ? 'primary.main' : 'action.hover',
                      color: isSelected ? 'primary.contrastText' : 'text.primary',
                    },
                  }}
                >
                  <Typography variant="body2" fontWeight={isSelected ? 600 : 400}>
                    {option.label}
                  </Typography>
                </MenuItem>
              );
            })}
            {options.length === 0 && (
              <MenuItem disabled>
                <Typography variant="body2" color="text.secondary">
                  選択肢がありません
                </Typography>
              </MenuItem>
            )}
          </Paper>
        </ClickAwayListener>
      </Popper>
    </FormControl>
  );
};

export default CustomSelect;
